import { form, TFormCodec } from "../../../shared/src/codecs/types/form";
import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { TTitleCheckDocumentFileCodec, TTitleCheckEditable, TTitleCheckEditableCodec, TTitleCheckReadOnly, TTitleCheckReadOnlyCodec, TitleCheckDocumentFile, TitleCheckEditable, TitleCheckReadOnly } from "../TitleCheck";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { CasePropertyNewTitleIssueForm, TCasePropertyNewTitleIssueFormCodec } from "./CasePropertyNewTitleIssueForm";
import { CasePropertyTitleIssue, TCasePropertyTitleIssueCodec } from "../CasePropertyTitleIssue";
import { array, TArrayCodec } from "../../../shared/src/codecs/types/array";
import { CasePropertyDeleteTitleIssueForm, TCasePropertyDeleteTitleIssueFormCodec } from "./CasePropertyDeleteTitleIssueForm";
import { alwaysFalse, alwaysTrue, notEmptyString, notUnknown } from "../../../shared/src/predicates";
import { DateTime } from "luxon";
import { pipe } from "fp-ts/lib/function";
import { record } from "fp-ts";
import { union } from "../../../shared/src/codecs/types/union";
import { literal } from "../../../shared/src/codecs/types/literal";

export const CaseTitleCheckForm: TFormCodec<
    TTitleCheckEditableCodec,
    TRequiredCodec<{
        read_only: TTitleCheckReadOnlyCodec,
        title_documents: TArrayCodec<TTitleCheckDocumentFileCodec>,
        title_check_issues: TArrayCodec<TCasePropertyTitleIssueCodec>,
        new_title_check_issue_form: TCasePropertyNewTitleIssueFormCodec,
        delete_title_check_issue_form: TCasePropertyDeleteTitleIssueFormCodec,
    }>
> = form(
    TitleCheckEditable,
    required({
        read_only: TitleCheckReadOnly,
        title_documents: array(TitleCheckDocumentFile),
        title_check_issues: array(CasePropertyTitleIssue),
        new_title_check_issue_form: CasePropertyNewTitleIssueForm,
        delete_title_check_issue_form: CasePropertyDeleteTitleIssueForm,
    }),
);
export type TCaseTitleCheckFormCodec = typeof CaseTitleCheckForm;
export type TCaseTitleCheckForm = TTypeOfCodec<TCaseTitleCheckFormCodec>;

export type TTitleCheckHowToFind = {
    whereToLook: string;
    searchFor: string | null;
};

type EditableCaseTitleCheckFieldsMeta = {
    [K in keyof TCaseTitleCheckForm["edited"]]: {
        shouldShow: (f: TTitleCheckEditable & TTitleCheckReadOnly) => boolean;
        isComplete: (val: TCaseTitleCheckForm["edited"][K]) => boolean;
        howToFind: TTitleCheckHowToFind | null;
    };
};


const isLeaseholdOrCommonhold = (p: TTitleCheckReadOnly) => p.tenure === "leasehold" || p.tenure === "commonhold"

export const editableCaseTitleCheckFieldsConfig: EditableCaseTitleCheckFieldsMeta = {
    id: {
        howToFind: null,
        shouldShow: alwaysFalse,
        isComplete: alwaysFalse,
    },
    last_transfer_date: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the date in brackets at the start of the PROPRIETOR entry. This is the date the current owners were registered.",
            searchFor: "PROPRIETOR:",
        },
        shouldShow: (f) => f.is_registered === true,
        isComplete: (v) => v !== null,
    },
    title_or_deed_obtained: {
        howToFind: {
            whereToLook: "The case Property block. Check an official copy has been fetched, with the register tile present and a fetched-at date set. For unregistered land, check the deeds bundle has arrived.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    all_documents_referred_to_in_titles_and_deeds_obtained: {
        howToFind: {
            whereToLook: "The Documents referred to on this title list in the Property block, cross-checked against the register. Entries ending NOTE: Copy filed are documents HMLR holds and can supply. Two kinds of entry cannot be obtained from HMLR. One notes No copy was supplied on first registration. The other notes that neither the original deed nor a certified copy or examined abstract thereof was produced on first registration.",
            searchFor: "Copy filed",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown
    },
    missing_deed_or_title_documents_description: {
        howToFind: {
            whereToLook: "List each register entry whose note says no copy was supplied or the deed was not produced on first registration. Give the register letter and entry number for each. Add any referred document HMLR has rejected as unavailable.",
            searchFor: "No copy",
        },
        shouldShow: (f) => f.all_documents_referred_to_in_titles_and_deeds_obtained === "some_missing",
        isComplete: notEmptyString,
    },
    can_epitome_of_title_be_drafted: {
        howToFind: {
            whereToLook: "Unregistered land only. The deeds bundle must give a good root of title at least 15 years old. That means a conveyance on sale or legal mortgage. It must also show an unbroken chain of ownership down to the seller.",
            searchFor: null,
        },
        shouldShow: (f) => f.is_registered === false,
        isComplete: notUnknown, // reconsititue if false
    },
    reconstituting_title: {
        howToFind: {
            whereToLook: "Only asked when no title or deeds were obtained, or the land is unregistered and no epitome can be drafted. Check the Property block shows no official copy and the deeds request has failed.",
            searchFor: null,
        },
        shouldShow: (f) => f.title_or_deed_obtained === "no" || (f.is_registered === false && f.can_epitome_of_title_be_drafted === "no"),
        isComplete: notUnknown, 
    },
    title_defects_that_require_indemnification: {
        howToFind: {
            whereToLook: "Start with the class of title in the register heading. Anything other than Title absolute counts. Then check any Proprietorship Register (B) restriction that is not a lender's. Also check Charges Register (C) entries noting a deed HMLR does not hold. An indemnity policy is the fallback when the defect cannot be cured by a deed of grant or retrospective consent.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    title_defects_description: {
        howToFind: {
            whereToLook: "Describe each defect by register letter and entry number, or by the missing deed. The indemnity broker needs this to quote against it.",
            searchFor: null,
        },
        shouldShow: (f) => f.title_defects_that_require_indemnification === "yes",
        isComplete: notEmptyString,
    },
    deed_of_variation_needed: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning Deed of Variation dated. A variation already made also shows in the Property Register (A) notes. For leaseholds, read the lease itself: the ground rent review clause, the forfeiture and mortgagee protection wording, and any defective rights. For freeholds, read the transfer or conveyance that imposed the covenants.",
            searchFor: "Deed of Variation",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    client_and_lender_has_approved_title_owned_less_than_6_months: {
        howToFind: {
            whereToLook: "Shown only when the last transfer date is within six months. Verify the trigger against the Proprietorship Register (B) PROPRIETOR entry date. Then take the answer from the lender's instructions and the client's written confirmation on file.",
            searchFor: "PROPRIETOR:",
        },
        shouldShow: (f) => f.last_transfer_date !== null && DateTime.utc().minus({months: 6}).toSeconds() < DateTime.fromISO(f.last_transfer_date).toUTC().toSeconds(), // LATER
        isComplete: notUnknown, // LATER allow no but make sure another roadblock takes it
    },
    registered_proprietors_names_match_sellers: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the PROPRIETOR entry. Compare it name by name with the sellers' legal names from their ID documents.",
            searchFor: "PROPRIETOR:",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    initial_reason_proprietors_names_do_not_match_sellers: {
        howToFind: {
            whereToLook: "Compare the Proprietorship Register (B) PROPRIETOR entry with the ID documents. The usual reasons are a deceased joint proprietor, a change of name, or a sale by personal representatives or an attorney.",
            searchFor: "PROPRIETOR:",
        },
        shouldShow: (f) => f.registered_proprietors_names_match_sellers === "no",
        isComplete: notEmptyString,
    },
    properties_more_than_10_years_old: {
        howToFind: {
            whereToLook: "This reads the title's first registration date, not the building's age. The earliest Property Register (A) entry date is when the title was first registered. A title registered within the last ten years is often a new build, but not always. Confirm the building's actual age from the EPC, the PIF or the lease before relying on a No here, because No is what asks for new-build warranties.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    new_build_warranties_provided: {
        howToFind: {
            whereToLook: "The seller's document pack, not the register. Look for the NHBC, Premier, LABC or equivalent warranty certificate and policy schedule, plus the building regulations final certificate.",
            searchFor: null,
        },
        shouldShow: (f) => f.properties_more_than_10_years_old === "no",
        isComplete: notUnknown // LATER another roadblock
    },
    right_of_way_acceptable: {
        howToFind: {
            whereToLook: "Property Register (A) entries beginning The land has the benefit of the following rights granted by. For leaseholds, also the lease's rights clause. Charges Register (C) entries beginning The land is subject to the following rights reserved by. These show rights others have over the property.",
            searchFor: "right of way",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    right_of_way_not_acceptable_reason: {
        howToFind: {
            whereToLook: "Quote the Property Register (A) entry number and say what is missing. For example, no right on foot and with vehicles, no maintenance obligation, or a right over land whose owner is unknown.",
            searchFor: "right of way",
        },
        shouldShow: (f) => f.right_of_way_acceptable === "no",
        isComplete: notEmptyString,
    },
    is_flying_freehold: {
        howToFind: {
            whereToLook: "Not answered automatically, because the register almost never says it. Compare the title plan with the building: a flying freehold is part of the property sitting above land belonging to someone else. The Property Register (A) description and any Charges Register (C) rights of support are the best written clues.",
            searchFor: null,
        },
        shouldShow: (f) => f.tenure === "freehold",
        isComplete: notUnknown,
    },
    flying_freehold_sufficient_rights_or_indemnity: {
        howToFind: {
            whereToLook: "Property Register (A) rights entries and the deed they refer to. Look for rights of support and protection and access for repair. If absent, a flying freehold indemnity policy.",
            searchFor: "right of support",
        },
        shouldShow: (f) => f.is_flying_freehold === "yes",
        isComplete: notUnknown
    },
    mines_minerals_excepted: {
        howToFind: {
            whereToLook: "Property Register (A), the Mines and Minerals entry. It reads The mines and minerals are excepted, or The mines and minerals together with ancillary powers of working are excepted. It can also read There are excluded from this registration the mines and minerals excepted by a Conveyance.",
            searchFor: "mines and minerals",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_form_a_restriction: {
        howToFind: {
            whereToLook: "Proprietorship Register (B). Look for the restriction beginning RESTRICTION: No disposition by a sole proprietor of the registered estate (except a trust corporation) under which capital money arises. Older titles use the wording of the land (not being a trust corporation).",
            searchFor: "sole proprietor",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_restrictive_covenants: {
        howToFind: {
            whereToLook: "Charges Register (C) entries beginning A Conveyance or A Transfer of the land in this title dated. They end contains covenants, contains restrictive covenants or contains the following covenants. Also the Schedule of restrictive covenants printed at the end of the Charges Register.",
            searchFor: "covenants",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_possessory_title: {
        howToFind: {
            whereToLook: "The class of title in the register heading and the Title class shown in the Property block, not the entries. HMLR records it as ClassOfTitleCode and the entries never say it.",
            searchFor: "Possessory",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_recent_proprietor_transfer: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the bracketed date at the start of the PROPRIETOR entry. Within six months of today counts as recent.",
            searchFor: "PROPRIETOR:",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning REGISTERED CHARGE dated. It is normally followed by a Proprietor entry naming the lender.",
            searchFor: "REGISTERED CHARGE",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_chargee: {
        howToFind: {
            whereToLook: "Charges Register (C), the entry immediately after a registered charge. It begins Proprietor: and names the lender and its address.",
            searchFor: "Proprietor:",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_sub_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry recording a charge of the registered charge itself. It is listed under the lender's charge.",
            searchFor: "sub-charge",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_multiple_charges: {
        howToFind: {
            whereToLook: "Charges Register (C), more than one entry beginning REGISTERED CHARGE dated.",
            searchFor: "REGISTERED CHARGE",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_noted_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), a notice of a charge that is not substantively registered. Typically an older or equitable mortgage protected by notice.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_discount_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), the right-to-buy discount charge. The entry begins A charge having the priority specified in section 156 of the Housing Act 1985 to secure the liability under the covenant to repay discount.",
            searchFor: "discount",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_equitable_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning Equitable charge created by an interim charging order of the County Court. It can also begin By a Mortgage Debenture dated the land is charged in equity.",
            searchFor: "equitable charge",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_charge_restriction: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the lender's restriction. It reads RESTRICTION: No disposition of the registered estate by the proprietor of the registered estate is to be registered without a written consent signed by the proprietor for the time being of the Charge dated.",
            searchFor: "proprietor for the time being of the Charge",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_charge_related_restriction: {
        howToFind: {
            whereToLook: "Charges Register (C), a restriction on the charge itself. It begins RESTRICTION: No disposition by the proprietor of the registered charge dated referred to above is to be registered without. It may instead begin Except under an order of the registrar no disposition (including an exercise of the power of sale).",
            searchFor: "proprietor of the registered charge",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_deed_of_postponement: {
        howToFind: {
            whereToLook: "Charges Register (C). Look for The priorities of the charges dated and referred to above have been altered by a Deed of Postponement dated. Also look for a note under a charge reading NOTE: This charge is postponed in priority to that dated.",
            searchFor: "postpone",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_agreed_notice: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning An Agreement dated or Agreement for lease dated made between. Also Property Register (A) section 49 notices beginning Notice of a Deed of Covenant dated.",
            searchFor: "Agreement dated",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_unilateral_notice: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning UNILATERAL NOTICE in respect of. It is followed by an entry beginning BENEFICIARY: naming who lodged it.",
            searchFor: "UNILATERAL NOTICE",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_right_of_pre_emption: {
        howToFind: {
            whereToLook: "Property Register (A) lease notes reading NOTE: The lease contains a right of pre-emption. Also look for a Charges Register (C) entry beginning Right of pre-emption in favour of contained in an Agreement dated.",
            searchFor: "pre-emption",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_creditors_notice: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), an entry beginning Creditors' Notice in respect of a petition in bankruptcy presented in the County Court.",
            searchFor: "Creditors' Notice",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_caution_entry: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), an entry beginning CAUTION in favour of. On caution titles, also Property Register (A) entries beginning The statement of truth accompanying the caution states the cautioner claims the following interest in the estate.",
            searchFor: "CAUTION",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_ccbi_entry: {
        howToFind: {
            whereToLook: "Proprietorship Register (B). This is HMLR's umbrella flag for court-order, caution, bankruptcy and inhibition entries. Read the whole of B for any entry that is not the proprietor, price paid or a restriction.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_bankruptcy_entry: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), an entry beginning BANKRUPTCY NOTICE entered under section 86(2) of the Land Registration Act 2002. Also look for an entry beginning Bankruptcy Inhibition in pursuance of a bankruptcy order made by the County Court.",
            searchFor: "BANKRUPTCY",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_home_rights: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning Notice of home rights under the Family Law Act 1996 in favour of. Older titles use Notice of rights of occupation under the Matrimonial Homes Act 1983 in favour of.",
            searchFor: "home rights",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_home_rights_change_of_address: {
        howToFind: {
            whereToLook: "Charges Register (C), a further entry beneath the home rights notice. It records a new address for service for the spouse or civil partner.",
            searchFor: "address for service",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_death_of_proprietor: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the PROPRIETOR entry or a note beneath it. Look for a proprietor described as deceased or for named personal representatives.",
            searchFor: "deceased",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_non_charge_restriction: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), any RESTRICTION: entry that is neither the lender's nor the Form A joint-proprietor one. Typically No disposition of the registered estate (other than a charge) by the proprietor is to be registered without a certificate signed by a management company. It can also be a personal representative restriction.",
            searchFor: "RESTRICTION:",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_rent_charge: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry beginning The land is subject to a perpetual yearly rentcharge of created by a Conveyance. Also check the Property Register (A) for rentcharge entries.",
            searchFor: "rentcharge",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_vendors_lien: {
        howToFind: {
            whereToLook: "Charges Register (C), an entry saying the Transfer referred to above contains provisions relating to the payment of additional money or further consideration.",
            searchFor: "payment of additional",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_price_paid_entry: {
        howToFind: {
            whereToLook: "Proprietorship Register (B), the entry The price stated to have been paid on [date] was £[amount].",
            searchFor: "price stated",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_green_out: {
        howToFind: {
            whereToLook: "Property Register (A), an entry beginning The land edged and numbered in green on the title plan has been removed from this title and registered under the title number. Also look for a note that land numbered on the plan is no longer of any significance and should be ignored.",
            searchFor: "removed from this title",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_schedule_of_leases: {
        howToFind: {
            whereToLook: "The end of the Charges Register (C), under the heading Schedule of notices of leases. Also the C entry The parts of the land affected thereby are subject to the leases set out in the schedule of leases hereto.",
            searchFor: "schedule of leases",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_property_description_note: {
        howToFind: {
            whereToLook: "Property Register (A), the NOTE lines printed beneath the property description or the lease particulars. They qualify what the title includes.",
            searchFor: "NOTE",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_unidentified_entry: {
        howToFind: {
            whereToLook: "No fixed wording. HMLR sets this when an entry does not fit any of its categories. Read every entry that is not a description, proprietor, price paid, restriction, charge, easement or covenant.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_scheme_title: {
        howToFind: {
            whereToLook: "Not answered automatically. Charges Register (C), an entry recording that the land falls within the area of a building scheme, which makes the estate covenants mutually enforceable between the properties in it.",
            searchFor: "scheme",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    has_caution_against_first_registration: {
        howToFind: {
            whereToLook: "Only on a caution title for unregistered land. The Proprietorship Register (B) shows CAUTIONER: instead of a proprietor. The Property Register (A) carries The statement of truth accompanying the caution states the cautioner claims the following interest in the estate.",
            searchFor: "CAUTIONER",
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    mines_minerals_excepted_relief: {
        howToFind: {
            whereToLook: "Not on the register. The compensation right, if any, is in the deed the Property Register (A) mines and minerals entry refers to. Order it from the Documents referred to on this title list. Otherwise a mines and minerals indemnity policy.",
            searchFor: "mines and minerals",
        },
        shouldShow: (f) => f.mines_minerals_excepted === "yes",
        isComplete: notUnknown,
    },
    leasehold_terms_sufficient_with_lender_and_client: {
        howToFind: {
            whereToLook: "Property Register (A), the entry Short particulars of the lease(s) (or under-lease(s)) under which the land is held, followed by Date : Term : Rent : Parties : lines. Measure the lease term remaining against the lender's minimum in the UK Finance Handbook Part 2.",
            searchFor: "Term :",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown,
    },
    annual_ground_rent_below_250_or_1000_for_london: {
        howToFind: {
            whereToLook: "Property Register (A) lease particulars, the Rent : part, and the lease's rent clause. £250 is the threshold, £1,000 inside Greater London.",
            searchFor: "Rent :",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown,
    },
    clause_cap_ground_rent_below_250_or_1000_for_london: {
        howToFind: {
            whereToLook: "The lease's rent review clause, or a deed of variation capping the rent. The register only hints at escalation where the lease particulars say rising to or subject to review.",
            searchFor: "rising to",
        },
        shouldShow: (f) => isLeaseholdOrCommonhold(f) && f.annual_ground_rent_below_250_or_1000_for_london === "no",
        isComplete: notUnknown, // LATER another roadblock
    },
    doubling_ground_rent: {
        howToFind: {
            whereToLook: "The lease's rent review clause. The register never says doubling, only rising to or rising as therein mentioned in the lease particulars.",
            searchFor: "rising",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown,
    },
    tenant_becomes_owner_of_fh_or_mc_member: {
        howToFind: {
            whereToLook: "The lease and the management company pack. A Proprietorship Register (B) restriction requiring a certificate from a management company means membership passes with the flat. The Property Register (A) note The landlord's title is registered tells you the reversion is registered.",
            searchFor: "management company",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown,
    },
    share_cert_obtained: {
        howToFind: {
            whereToLook: "The seller's management company pack, not the register. Look for the share or membership certificate and a signed stock transfer form.",
            searchFor: null,
        },
        shouldShow: (f) => f.tenant_becomes_owner_of_fh_or_mc_member === "management_company_member" || f.tenant_becomes_owner_of_fh_or_mc_member === "freehold_owner",
        isComplete: notUnknown // LATER another roadblock
    },
    sufficient_repair_covenants: {
        howToFind: {
            whereToLook: "The lease's lessee and lessor covenants. For a flat or maisonette, the landlord or management company should repair the structure and common parts. For a house, the lessee usually repairs everything.",
            searchFor: null,
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown // LATER another roadblock
    },
    lease_contains_provision_for_forfeiture_on_bankruptcy: {
        howToFind: {
            whereToLook: "The lease's forfeiture or re-entry clause. The register will not show it. A right to forfeit on the tenant's bankruptcy is unacceptable to lenders.",
            searchFor: null,
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown
    },
    sufficient_rights_of_support: {
        howToFind: {
            whereToLook: "The lease's rights clause. Also any Property Register (A) rights entry mentioning right of support, shelter and protection.",
            searchFor: "support",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown // LATER another roadblock
    },
    sufficient_rights_of_access: {
        howToFind: {
            whereToLook: "The lease's rights clause. The Property Register (A) may carry the note The title includes any legal easements referred to in clause LR11.1 of the registered lease. That means the rights are in the lease rather than set out on the register.",
            searchFor: "right of way",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown // LATER another roadblock
    },
    landlord_insurers_building: {
        howToFind: {
            whereToLook: "The lease's insurance clause and the current buildings insurance policy and schedule. A flat or maisonette is normally insured by the landlord or management company, and a house by the lessee. Lease particulars quoting an insurance rent point to landlord insurance.",
            searchFor: "insurance rent",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown // LATER another roadblock
    },
    deed_of_covenant_required: {
        howToFind: {
            whereToLook: "The lease's alienation clause. The Property Register (A) entry The Lease prohibits or restricts alienation is HMLR's flag that assignment is restricted. The Proprietorship Register (B) may carry the entry The Transfer to the proprietor contains a covenant to observe and perform the covenants referred to in the Charges Register and of indemnity in respect thereof. That means a chain of indemnity covenants, not a deed of covenant.",
            searchFor: "alienation",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown
    },
    license_to_assign_required: {
        howToFind: {
            whereToLook: "The lease's alienation clause. The Property Register (A) entry The Lease prohibits or restricts alienation tells you consent or a covenant is needed. It does not say which.",
            searchFor: "alienation",
        },
        shouldShow: isLeaseholdOrCommonhold,
        isComplete: notUnknown
    },
    // Retired from the title check. Its evidence is the PIF, the TA6 and the local
    // authority search, none of which exist before an offer, so on a probate sale it
    // asked a personal representative who never lived there and the check could never
    // complete. The covenant half of the question survives as
    // planning_regs_require_consent_or_transfer, which the register does answer.
    building_regs_planning_structural_guarantee_required: {
        howToFind: {
            whereToLook: "Not the register. Use the PIF answers on alterations and extensions, and the planning and building regulations documents in the seller's pack. Also any Charges Register (C) covenant requiring consent to build or alter.",
            searchFor: "planning",
        },
        shouldShow: alwaysFalse,
        isComplete: notUnknown,
    },
    planning_regs_require_consent_or_transfer: {
        howToFind: {
            whereToLook: "Charges Register (C) restrictive covenants requiring the transferor's or estate owner's consent to build or alter, and the planning history. Consent means a retrospective consent is needed. Transfer means a deed or indemnity must pass on completion.",
            searchFor: "consent",
        },
        shouldShow: (f) => f.building_regs_planning_structural_guarantee_required === "yes",
        isComplete: notUnknown,
    },
    property_type: {
        howToFind: {
            whereToLook: "Not on the register. Use the listing, EPC or PIF. The Property Register (A) property description is only a hint. It names a flat, apartment or floor for a unit within a building.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    insurance_sufficient_for_maisonette: {
        howToFind: {
            whereToLook: "The current buildings insurance policy and schedule from the landlord or management company. Check it against the lease's insurance covenant and the reinstatement value.",
            searchFor: null,
        },
        shouldShow: (f) => isLeaseholdOrCommonhold(f) && f.landlord_insurers_building === "yes" && f.property_type === "maisonette",
        isComplete: notUnknown,
    },
    all_charges_and_restrictions_for_title_added: {
        howToFind: {
            whereToLook: "Compare the Charges & Restrictions block with the Charges Register (C) REGISTERED CHARGE entries. Also compare it with every RESTRICTION: in the Proprietorship (B) and Charges (C) Registers. Then stamp it.",
            searchFor: "RESTRICTION:",
        },
        shouldShow: alwaysTrue,
        isComplete: (v) => v !== null,
    },
    has_other_title_issues: {
        howToFind: {
            whereToLook: "The whole register, for anything the checklist does not name. Look for Property Register (A) provisions about light, air and boundary structures. Also copyhold and manorial notes, chancel repair liability and exclusive use of parking. Overriding interests never appear on the register at all.",
            searchFor: null,
        },
        shouldShow: alwaysTrue,
        isComplete: notUnknown,
    },
    all_other_title_issues_added: {
        howToFind: {
            whereToLook: "Confirm the Other Title Issues list in this panel names every entry you flagged. Then stamp it.",
            searchFor: null,
        },
        shouldShow: (f) => f.has_other_title_issues === "yes",
        isComplete: (v) => v !== null,
    },
};

export const caseTitleCheckFieldHelp: Record<string, TTitleCheckHowToFind> = pipe(
    editableCaseTitleCheckFieldsConfig,
    record.reduceWithIndex({}, (fieldName, acc: Record<string, TTitleCheckHowToFind>, funcs) =>
        funcs.howToFind === null
            ? acc
            : { ...acc, [fieldName]: funcs.howToFind }
    ),
);

export const getCaseTitleCheckFieldsThatShouldBeHidden = (p: TTitleCheckEditable & TTitleCheckReadOnly): (keyof TCaseTitleCheckForm["edited"])[] => pipe(
    editableCaseTitleCheckFieldsConfig,
    record.reduceWithIndex([], (index, acc: (keyof TCaseTitleCheckForm["edited"])[], funcs) => {
        const shouldShow = funcs.shouldShow(p);
        if (!shouldShow) {
            return [...acc, index];
        }
        return acc;
    })
);

export const AUTO_DETECTED_TITLE_CHECK_FIELDS: ReadonlySet<keyof TCaseTitleCheckForm["edited"]> = new Set<keyof TCaseTitleCheckForm["edited"]>([
    "has_form_a_restriction",
    "mines_minerals_excepted",
    "has_restrictive_covenants",
    "has_possessory_title",
    "has_recent_proprietor_transfer",
    "title_or_deed_obtained",
    "is_flying_freehold",
    "properties_more_than_10_years_old",
    "has_charge",
    "has_chargee",
    "has_sub_charge",
    "has_multiple_charges",
    "has_noted_charge",
    "has_discount_charge",
    "has_equitable_charge",
    "has_charge_restriction",
    "has_charge_related_restriction",
    "has_deed_of_postponement",
    "has_agreed_notice",
    "has_unilateral_notice",
    "has_right_of_pre_emption",
    "has_creditors_notice",
    "has_caution_entry",
    "has_ccbi_entry",
    "has_bankruptcy_entry",
    "has_home_rights",
    "has_home_rights_change_of_address",
    "has_death_of_proprietor",
    "has_non_charge_restriction",
    "has_rent_charge",
    "has_vendors_lien",
    "has_price_paid_entry",
    "has_green_out",
    "has_schedule_of_leases",
    "has_property_description_note",
    "has_unidentified_entry",
    "has_caution_against_first_registration",
    "title_defects_that_require_indemnification",
    "reconstituting_title",
    "can_epitome_of_title_be_drafted",
    "right_of_way_acceptable",
    "registered_proprietors_names_match_sellers",
]);

export type TCaseTitleCheckFieldGroup = "manual" | "flagged" | "clean";

export const fieldGroup = (
    fieldName: keyof TCaseTitleCheckForm["edited"],
    p: TTitleCheckEditable,
): TCaseTitleCheckFieldGroup => {
    if (!AUTO_DETECTED_TITLE_CHECK_FIELDS.has(fieldName)) return "manual";
    const value = p[fieldName];
    if (value === "yes") return "flagged";
    if (value === "no") return "clean";
    return "manual";
};

export const getCaseTitleCheckFieldsToHideForGroup = (
    p: TTitleCheckEditable & TTitleCheckReadOnly,
    group: TCaseTitleCheckFieldGroup,
): (keyof TCaseTitleCheckForm["edited"])[] => pipe(
    editableCaseTitleCheckFieldsConfig,
    record.reduceWithIndex([], (index, acc: (keyof TCaseTitleCheckForm["edited"])[], funcs) => {
        const shouldShow = funcs.shouldShow(p);
        if (!shouldShow) return [...acc, index];
        if (fieldGroup(index, p) !== group) return [...acc, index];
        return acc;
    })
);

export const getCaseTitleCheckFieldGroupCounts = (
    p: TTitleCheckEditable & TTitleCheckReadOnly,
): { manual: number, flagged: number, clean: number } => pipe(
    editableCaseTitleCheckFieldsConfig,
    record.reduceWithIndex({ manual: 0, flagged: 0, clean: 0 }, (index, acc, funcs) => {
        if (!funcs.shouldShow(p)) return acc;
        const group = fieldGroup(index, p);
        return { ...acc, [group]: acc[group] + 1 };
    }),
);

export const CaseTitleCheckFormStatus = union([literal("pending"), literal("done")]);
export type TCaseTitleCheckFormStatusCodec = typeof CaseTitleCheckFormStatus;
export type TCaseTitleCheckFormStatus = TTypeOfCodec<TCaseTitleCheckFormStatusCodec>;

export type TCaseTitleCheckFormState = {
    complete: number;
    total: number;
    status: TCaseTitleCheckFormStatus;
};

export const getCompletedStateTupleFromData = (p: TTitleCheckEditable & TTitleCheckReadOnly): TCaseTitleCheckFormState => {
    return pipe(
        editableCaseTitleCheckFieldsConfig,
        record.reduceWithIndex({
            complete: 0,
            total: 0,
            status: "pending"
        } as TCaseTitleCheckFormState, (index, acc, funcs) => {
            const shouldShow = funcs.shouldShow(p);
            const isComplete = funcs.isComplete as (v: unknown) => boolean; // Don't know why I have to do this
            if (!shouldShow) {
                return acc;
            }
            const currentVal = p[index];
            const complete = isComplete(currentVal) ? acc.complete + 1 : acc.complete;
            const total = acc.total + 1;

            return {
                complete,
                total,
                status: complete === total ? "done" : "pending"
            };
        })
    )
} 